import { useEffect, useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import toast from 'react-hot-toast'
import {
  disablePushNotifications,
  enablePushNotifications,
  getLocalPushState,
  getSubscriptionCount,
  isIosDevice,
  isPushSupported,
  isStandaloneDisplay,
  registerServiceWorker,
  sendTestPush,
} from '../utils/pushNotifications'

export default function PushNotificationSettings() {
  const { getAccessTokenSilently, isAuthenticated } = useAuth0()
  const [supported, setSupported] = useState(false)
  const [permission, setPermission] = useState<NotificationPermission | 'unsupported'>('default')
  const [subscribed, setSubscribed] = useState(false)
  const [serverCount, setServerCount] = useState(0)
  const [busy, setBusy] = useState(false)
  const iosNeedsInstall = isIosDevice() && !isStandaloneDisplay()

  const refresh = async () => {
    const local = await getLocalPushState()
    setSupported(local.supported)
    setPermission(local.permission)
    setSubscribed(local.subscribed)

    if (isAuthenticated) {
      try {
        const count = await getSubscriptionCount(getAccessTokenSilently)
        setServerCount(count)
      } catch {
        setServerCount(0)
      }
    }
  }

  useEffect(() => {
    if (!isPushSupported()) {
      setSupported(false)
      setPermission('unsupported')
      return
    }

    registerServiceWorker().finally(() => {
      refresh()
    })
  }, [isAuthenticated, getAccessTokenSilently])

  const handleEnable = async () => {
    setBusy(true)
    try {
      await enablePushNotifications(getAccessTokenSilently)
      toast.success('Notifications enabled')
      await refresh()
    } catch (error: any) {
      toast.error(error?.message || 'Could not enable notifications')
    } finally {
      setBusy(false)
    }
  }

  const handleDisable = async () => {
    setBusy(true)
    try {
      await disablePushNotifications(getAccessTokenSilently)
      toast.success('Notifications disabled')
      await refresh()
    } catch (error: any) {
      toast.error(error?.message || 'Could not disable notifications')
    } finally {
      setBusy(false)
    }
  }

  const handleTest = async () => {
    setBusy(true)
    try {
      await sendTestPush(getAccessTokenSilently)
      toast.success('Test notification sent')
    } catch (error: any) {
      toast.error(error?.message || 'Test failed')
    } finally {
      setBusy(false)
    }
  }

  if (!supported && !isIosDevice()) {
    return (
      <div className="p-6 bg-gray-50 dark:bg-gray-700 rounded-xl">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Notifications</h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Push notifications are not supported in this browser.
        </p>
      </div>
    )
  }

  return (
    <div className="p-6 bg-gray-50 dark:bg-gray-700 rounded-xl space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Notifications</h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Get alerts for club updates, events, and (soon) group chat.
        </p>
      </div>

      {iosNeedsInstall && (
        <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-900/30 dark:border-amber-700 px-4 py-3 text-sm text-amber-900 dark:text-amber-100">
          <p className="font-medium mb-1">iPhone setup</p>
          <ol className="list-decimal list-inside space-y-1 opacity-90">
            <li>Open this site in Safari</li>
            <li>Tap Share → Add to Home Screen</li>
            <li>Open Midwest Railbirds from the new Home Screen icon</li>
            <li>Come back here and tap Enable notifications</li>
          </ol>
        </div>
      )}

      <div className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
        <p>
          Status:{' '}
          <span className="font-medium text-gray-900 dark:text-white">
            {subscribed || serverCount > 0 ? 'Enabled on this account' : 'Not enabled'}
          </span>
        </p>
        <p>
          Permission:{' '}
          <span className="font-medium text-gray-900 dark:text-white">{permission}</span>
        </p>
        {serverCount > 0 && (
          <p>
            Devices registered: <span className="font-medium text-gray-900 dark:text-white">{serverCount}</span>
          </p>
        )}
      </div>

      <div className="flex flex-wrap gap-3">
        {!subscribed ? (
          <button
            type="button"
            onClick={handleEnable}
            disabled={busy || iosNeedsInstall}
            className="inline-flex items-center px-4 py-2 rounded-xl text-sm font-medium text-white bg-gradient-to-r from-green-600 to-green-400 hover:from-green-700 hover:to-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {busy ? 'Working…' : 'Enable notifications'}
          </button>
        ) : (
          <button
            type="button"
            onClick={handleDisable}
            disabled={busy}
            className="inline-flex items-center px-4 py-2 rounded-xl text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 ring-1 ring-gray-300 dark:ring-gray-600 hover:bg-gray-50 disabled:opacity-50"
          >
            Disable on this device
          </button>
        )}

        <button
          type="button"
          onClick={handleTest}
          disabled={busy || (!subscribed && serverCount === 0)}
          className="inline-flex items-center px-4 py-2 rounded-xl text-sm font-medium text-white bg-gray-800 dark:bg-gray-900 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Send test push
        </button>
      </div>
    </div>
  )
}
